import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/pages/OrdersPage";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminPayments from "@/pages/AdminPayments";
import AdminPosts from "@/pages/AdminPosts";
import AdminCampaigns from "@/pages/AdminCampaigns";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

function Stat({ label, value, testId }) {
  return (
    <div className="border-b sm:border-b-0 sm:border-r border-slate-100 last:border-0 px-6 py-5">
      <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p data-testid={testId} className="font-heading text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

const SELLER_STATUSES = ["ACTIVE", "RESTRICTED", "SUSPENDED", "TERMINATED"];

function NewCategoryDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", image: "", commission_rate: 0.10 });

  const submit = async () => {
    try {
      await api.post("/admin/categories", { ...f, commission_rate: Number(f.commission_rate) });
      toast.success("Category created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-category-btn" className="rounded-md bg-slate-900">New category</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create category</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="cat-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Category name" className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
          <input data-testid="cat-image" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} placeholder="Image URL" className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
        </div>
        <Button data-testid="cat-submit" onClick={submit} disabled={!f.name} className="rounded-md bg-slate-900 mt-2">Create</Button>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDialog({ seller, onDone }) {
  const [open, setOpen] = useState(false);
  const [fee, setFee] = useState(5000);

  const submit = async () => {
    try {
      await api.post(`/admin/sellers/${seller.id}/approve`, { platform_fee: Number(fee) });
      toast.success("Vendor approved — awaiting fee payment");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`approve-seller-${seller.id}`} size="sm" className="rounded-md bg-slate-900">Approve</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Approve {seller.business_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Set the one-time platform admission fee. The vendor pays it from their portal, then you admit them to start listing. Hello Nanba takes no commission on their sales.</p>
        <label className="text-xs font-semibold text-slate-500 block">Platform fee (₹)
          <input data-testid="approve-fee-input" type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
        </label>
        <Button data-testid="approve-seller-submit" onClick={submit} className="rounded-md bg-slate-900 mt-2">Approve vendor</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPortal() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [purgeArmed, setPurgeArmed] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, sl, c, o] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/sellers"), api.get("/categories"), api.get("/admin/orders"),
      ]);
      setStats(s.data); setSellers(sl.data); setCategories(c.data); setOrders(o.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/admin/login", { state: { from: "/admin" } });
    if (user.role !== "admin") return nav("/");
    load();
  }, [user, loading, nav, load]);

  if (!stats) return <div className="max-w-7xl mx-auto px-8 mt-12"><div className="h-96 bg-slate-100 rounded-2xl animate-pulse" /></div>;

  const purgeData = async () => {
    try {
      await api.post("/admin/purge-data", { confirm: "PURGE_ALL_DATA" });
      toast.success("All demo/test data purged");
      setPurgeArmed(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const admitSeller = async (id) => {
    try {
      await api.post(`/admin/sellers/${id}/admit`);
      toast.success("Vendor admitted — listing enabled");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const setSellerStatus = async (id, status) => {
    try {
      await api.post(`/admin/sellers/${id}/status`, { status });
      toast.success(`Seller → ${status.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="admin-portal" className="max-w-7xl mx-auto px-4 sm:px-8 mt-10">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Marketplace Control Centre</h1>
      <p className="text-sm text-slate-500 mt-1">Economics, operations, sellers and risk — one view.</p>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger data-testid="tab-admin-overview" value="overview">Overview</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-analytics" value="analytics">Analytics</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-payments" value="payments">Payments</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-posts" value="posts">Blog / Posts</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-campaigns" value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-sellers" value="sellers">Vendors {stats.sellers_pending > 0 && <span className="ml-1.5 bg-amber-500 text-white text-[10px] rounded-full px-1.5">{stats.sellers_pending}</span>}</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-categories" value="categories">Categories</TabsTrigger>
          <TabsTrigger data-testid="tab-admin-orders" value="orders">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-slate-100 rounded-xl overflow-hidden bg-slate-50/50">
            <Stat label="GMV" value={inr(stats.gmv)} testId="admin-gmv" />
            <Stat label="Orders" value={stats.orders} testId="admin-orders" />
            <Stat label="AOV" value={inr(stats.aov)} testId="admin-aov" />
            <Stat label="Customers" value={stats.customers} testId="admin-customers" />
            <Stat label="Vendors" value={stats.sellers_total} testId="admin-sellers" />
            <Stat label="Platform fee revenue" value={inr(stats.revenue)} testId="admin-revenue" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 border border-slate-100 rounded-xl overflow-hidden bg-slate-50/50 mt-6">
            <Stat label="Cancel rate" value={`${stats.cancel_rate}%`} testId="admin-cancel-rate" />
            <Stat label="Return rate" value={`${stats.return_rate}%`} testId="admin-return-rate" />
            <Stat label="Pending returns" value={stats.returns_pending} testId="admin-returns-pending" />
            <Stat label="Reviews" value={stats.reviews} testId="admin-reviews" />
          </div>
          <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-6">
            <div className="border border-slate-100 rounded-xl p-6">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-4">GMV (last 14 days)</p>
              {stats.gmv_series.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={stats.gmv_series}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => inr(v)} />
                    <Line type="monotone" dataKey="gmv" stroke="#0f172a" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 py-16 text-center">No sales yet.</p>}
            </div>
            <div className="border border-slate-100 rounded-xl p-6">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-4 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Risk signals</p>
              {stats.risk.length ? stats.risk.map((r, i) => (
                <div key={i} data-testid={`risk-flag-${i}`} className="py-2.5 border-b border-slate-50 last:border-0">
                  <p className="text-xs font-bold text-red-600">{r.flag.replace(/_/g, " ")}</p>
                  <p className="text-sm font-semibold">{r.seller}</p>
                  <p className="text-xs text-slate-400">{r.detail}</p>
                </div>
              )) : <p className="text-sm text-slate-400">No risk signals detected.</p>}
            </div>
          </div>
          <div className="mt-10 border border-red-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading font-bold text-red-600 text-sm">Danger zone</p>
              <p className="text-xs text-slate-500 mt-1">Permanently deletes all orders, vendors, products, customers, reviews and settlements. Admin accounts and categories are kept.</p>
            </div>
            <Button
              data-testid="purge-data-btn"
              variant="outline"
              className={purgeArmed ? "rounded-md bg-red-600 text-white hover:bg-red-500 border-red-600" : "rounded-md text-red-600 border-red-200"}
              onClick={() => {
                if (purgeArmed) purgeData();
                else {
                  setPurgeArmed(true);
                  setTimeout(() => setPurgeArmed(false), 5000);
                }
              }}
            >
              {purgeArmed ? "Click again to confirm purge" : "Purge all demo/test data"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AdminAnalytics />
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <AdminPayments />
        </TabsContent>

        <TabsContent value="posts" className="mt-6">
          <AdminPosts />
        </TabsContent>

        <TabsContent value="campaigns" className="mt-6">
          <AdminCampaigns />
        </TabsContent>

        <TabsContent value="sellers" className="mt-6">
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Business</TableHead><TableHead>Owner</TableHead><TableHead>Tax ID</TableHead><TableHead>Bank</TableHead><TableHead>Rating</TableHead><TableHead>Platform fee</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {sellers.map((s) => (
                  <TableRow key={s.id} data-testid={`admin-seller-row-${s.id}`}>
                    <TableCell><p className="font-semibold text-sm">{s.business_name}</p><p className="text-[11px] text-slate-400">{s.legal_entity}</p></TableCell>
                    <TableCell className="text-sm">{s.owner_name}</TableCell>
                    <TableCell className="font-mono text-xs">{s.tax_id}</TableCell>
                    <TableCell className="font-mono text-xs">{s.bank_account}</TableCell>
                    <TableCell className="font-semibold">{s.rating}</TableCell>
                    <TableCell className="text-xs">
                      {s.platform_fee != null ? inr(s.platform_fee) : "—"}
                      {s.fee_paid && <span className="block text-emerald-600 font-bold">Paid</span>}
                    </TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell>
                      {s.status === "UNDER_REVIEW" && <ApproveDialog seller={s} onDone={load} />}
                      {s.status === "APPROVED" && <span className="text-xs text-slate-400">Awaiting fee payment</span>}
                      {s.status === "FEE_PAID" && (
                        <Button data-testid={`admit-seller-${s.id}`} size="sm" onClick={() => admitSeller(s.id)} className="rounded-md bg-emerald-600 hover:bg-emerald-500">
                          Admit to platform
                        </Button>
                      )}
                      {["ACTIVE", "RESTRICTED", "SUSPENDED", "TERMINATED"].includes(s.status) && (
                        <Select value={s.status} onValueChange={(v) => setSellerStatus(s.id, v)}>
                          <SelectTrigger data-testid={`seller-status-select-${s.id}`} className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SELLER_STATUSES.map((st) => <SelectItem key={st} value={st}>{st.replace(/_/g, " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!sellers.length && <TableRow><TableCell colSpan={8} className="text-center py-16 text-slate-400 text-sm">No vendor applications yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="flex justify-end mb-4"><NewCategoryDialog onDone={load} /></div>
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Image</TableHead></TableRow></TableHeader>
              <TableBody>
                {categories.map((c) => (
                  <TableRow key={c.id} data-testid={`admin-category-row-${c.id}`}>
                    <TableCell className="font-semibold text-sm">{c.name}</TableCell>
                    <TableCell className="text-xs text-slate-400 max-w-[280px] truncate">{c.image || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Date</TableHead><TableHead>Items</TableHead><TableHead>Payment</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-row-${o.id}`}>
                    <TableCell className="font-mono text-xs">#{o.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell className="text-sm">{new Date(o.created_at).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell className="text-sm">{o.items.length}</TableCell>
                    <TableCell className="text-xs font-semibold">{o.payment.status}</TableCell>
                    <TableCell className="font-bold">{inr(o.total)}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                  </TableRow>
                ))}
                {!orders.length && <TableRow><TableCell colSpan={6} className="text-center py-16 text-slate-400 text-sm">No orders yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
